/**
 * ApplicationsTab — Review incoming school applications, approve/reject.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, XCircle, Clock, Mail, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { superAdminApi } from './superAdminApi';

const STATUS_COLORS = {
  submitted: 'bg-sky-500/20 text-sky-300 border-sky-400/30',
  under_review: 'bg-amber-500/20 text-amber-300 border-amber-400/30',
  approved: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30',
  rejected: 'bg-rose-500/20 text-rose-300 border-rose-400/30',
};

export const ApplicationsTab = () => {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('all');
  const [reviewing, setReviewing] = useState(null);

  const { data: apps = [], isLoading } = useQuery({
    queryKey: ['superadmin-applications', filter],
    queryFn: () => superAdminApi.listApplications(filter === 'all' ? null : filter),
  });

  const { data: plans = [] } = useQuery({
    queryKey: ['superadmin-plans'],
    queryFn: () => superAdminApi.listPlans(false),
  });

  const reviewMut = useMutation({
    mutationFn: ({ id, data }) => superAdminApi.reviewApplication(id, data),
    onSuccess: (r) => {
      toast.success(r.status === 'approved' ? `Approved → Tenant ${r.tenant_id} created` : `Application ${r.status}`);
      setReviewing(null);
      qc.invalidateQueries({ queryKey: ['superadmin-applications'] });
      qc.invalidateQueries({ queryKey: ['superadmin-tenants'] });
      qc.invalidateQueries({ queryKey: ['superadmin-dashboard'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Review failed'),
  });

  return (
    <div className="space-y-4" data-testid="superadmin-applications-tab">
      <div className="flex items-center gap-2">
        {['all', 'submitted', 'under_review', 'approved', 'rejected'].map(s => (
          <Button key={s} size="sm"
                  variant={filter === s ? 'default' : 'outline'}
                  onClick={() => setFilter(s)}
                  className={filter === s ? 'bg-indigo-500 hover:bg-indigo-400 text-white' : 'text-white border-white/20 bg-white/5 hover:bg-white/10'}
                  data-testid={`apps-filter-${s}`}>
            <span className="capitalize">{s.replace('_', ' ')}</span>
          </Button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {isLoading && <p className="text-slate-500">Loading...</p>}
        {!isLoading && apps.length === 0 && <p className="text-slate-500 text-sm col-span-2">No applications found.</p>}
        {apps.map(app => (
          <Card key={app.id} className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
            <CardContent className="pt-5 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-bold text-lg">{app.school_name}</h4>
                  <p className="text-xs text-slate-400 capitalize">{app.institution_type}</p>
                </div>
                <Badge variant="outline" className={`capitalize ${STATUS_COLORS[app.status] || ''}`}>
                  {app.status?.replace('_', ' ')}
                </Badge>
              </div>
              <div className="space-y-1 text-sm">
                <p className="text-slate-300 flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-500" /> {app.contact_email}</p>
                {app.contact_phone && <p className="text-slate-300 flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-500" /> {app.contact_phone}</p>}
                {(app.city || app.country) && <p className="text-slate-300 flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-slate-500" /> {[app.city, app.country].filter(Boolean).join(', ')}</p>}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs bg-white/5 rounded-lg p-2 border border-white/5">
                <div><p className="text-slate-500">Contact</p><p className="font-semibold">{app.contact_person}</p></div>
                <div><p className="text-slate-500">Students</p><p className="font-semibold">{app.estimated_students || '—'}</p></div>
                <div><p className="text-slate-500">Plan</p><p className="font-semibold uppercase">{app.preferred_plan || '—'}</p></div>
              </div>
              {app.message && <p className="text-xs text-slate-400 italic bg-white/5 rounded p-2 border-l-2 border-indigo-400/50">"{app.message}"</p>}
              <p className="text-[10px] text-slate-600"><Clock className="inline h-3 w-3 mr-1" />
                {app.created_at ? new Date(app.created_at).toLocaleString() : ''}
              </p>
              {app.status === 'submitted' || app.status === 'under_review' ? (
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => setReviewing({ ...app, _decision: 'approve' })}
                          className="bg-emerald-500 hover:bg-emerald-400 text-white flex-1"
                          data-testid={`app-approve-${app.id}`}>
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setReviewing({ ...app, _decision: 'reject' })}
                          className="text-rose-300 border-rose-400/30 bg-rose-500/10 hover:bg-rose-500/20 flex-1"
                          data-testid={`app-reject-${app.id}`}>
                    <XCircle className="h-4 w-4 mr-1" /> Reject
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-slate-500 pt-2 border-t border-white/5">
                  {app.status === 'approved' && app.tenant_id && `✓ Tenant ID: ${app.tenant_id}`}
                  {app.status === 'rejected' && `Rejected${app.review_reason ? `: ${app.review_reason}` : ''}`}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {reviewing && (
        <ReviewDialog
          app={reviewing}
          plans={plans}
          onClose={() => setReviewing(null)}
          onSubmit={(data) => reviewMut.mutate({ id: reviewing.id, data })}
          submitting={reviewMut.isPending}
        />
      )}
    </div>
  );
};

const ReviewDialog = ({ app, plans, onClose, onSubmit, submitting }) => {
  const [reason, setReason] = useState('');
  const [planCode, setPlanCode] = useState(app.preferred_plan || 'trial');
  const [trialDays, setTrialDays] = useState(14);
  const isApprove = app._decision === 'approve';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 text-white border-white/10">
        <DialogHeader>
          <DialogTitle className={isApprove ? 'text-emerald-300' : 'text-rose-300'}>
            {isApprove ? 'Approve Application' : 'Reject Application'}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {isApprove ? `Provision a tenant for "${app.school_name}"` : `Reject "${app.school_name}"'s application`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {isApprove && (
            <>
              <div>
                <Label>Plan</Label>
                <Select value={planCode} onValueChange={setPlanCode}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="review-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {plans.map(p => <SelectItem key={p.code} value={p.code}>{p.name} — ${p.price_monthly}/mo</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Trial Days</Label>
                <Input type="number" value={trialDays}
                       onChange={e => setTrialDays(parseInt(e.target.value) || 0)}
                       className="bg-white/5 border-white/10 text-white" data-testid="review-trial-days" />
              </div>
            </>
          )}
          <div>
            <Label>{isApprove ? 'Notes (optional)' : 'Reason *'}</Label>
            <Input value={reason} onChange={e => setReason(e.target.value)}
                   placeholder={isApprove ? 'Welcome note...' : 'Why rejecting?'}
                   className="bg-white/5 border-white/10 text-white" data-testid="review-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="text-white border-white/20 bg-white/5">Cancel</Button>
          <Button onClick={() => onSubmit({
                    decision: app._decision,
                    reason,
                    plan_code: isApprove ? planCode : null,
                    trial_days: trialDays,
                  })}
                  disabled={submitting || (!isApprove && !reason)}
                  className={isApprove ? 'bg-emerald-500 hover:bg-emerald-400 text-white' : 'bg-rose-500 hover:bg-rose-400 text-white'}
                  data-testid="review-confirm-btn">
            {submitting ? 'Processing...' : (isApprove ? 'Approve & Provision' : 'Reject')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
